/**
 * @architecture-header 1
 * @layer routes
 * @domain notifications
 * @responsibility Render the phone-native inbox page (收件匣: filters, loading state, notification
 *   deep links) plus, as an additive second tab, SH-F09's own 有什麼新的 static product-updates
 *   list -- mirroring Desktop's InboxPopover, which pairs the exact same two independent data
 *   sources behind one shared trigger. The two tabs share this page purely for entry-point parity;
 *   收件匣's own query/filter/mark-all-read logic is untouched by the 有什麼新的 tab.
 * @state-owner notifications query owns 收件匣 items; local state owns display filters and the
 *   active tab; product-updates module owns the 有什麼新的 seen-cursor (localStorage, no network)
 * @allowed-imports React; contracts; app providers; notifications and product-updates module
 *   public APIs; shared stores/components
 * @forbidden desktop inbox tabs/popover component import (this page's own tab strip is a separate,
 *   phone-native implementation, not a reuse of ShellPopovers' InboxPopover markup); direct API
 *   client; invented notification fields
 * @public-api MobileWorkspaceShell
 * @side-effects marks notifications read, navigates to the closest exact workspace target, and
 *   marks product updates seen when this page closes while 有什麼新的 was the active tab
 * @tests frontend/apps/web/e2e/workspace.spec.ts; frontend/apps/web/e2e/sh-f09.spec.ts
 */

import type { InboxItem } from '@vispo/contracts';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useActiveContextId } from '../../app/providers/ActiveRoleProvider';
import { useWorkspaceSession } from '../../app/providers/SessionProvider';
import {
  getInboxItemTargetPath,
  useInbox,
  useMarkAllInboxRead,
  useMarkInboxRead,
} from '../../modules/notifications';
import { localizedProductUpdateText, useProductUpdatesCursor } from '../../modules/product-updates';
import { MainEmpty } from '../../shared/components/ListColumn';
import { FilterIcon } from '../../shared/components/VispoIcon';
import { VispoLoading } from '../../shared/components/WorkspaceState';
import { useDatasetStore } from '../../shared/model/dataset-store';
import { useTranslator } from '../../shared/model/use-workspace';
import { useWorkspaceUiStore } from '../../shared/model/workspace-store';
import { MobilePageFrame } from './MobilePageFrame';

interface InboxFilters {
  unreadFirst: boolean;
  showRead: boolean;
  showCompleted: boolean;
  announcementsOnly: boolean;
}

const INITIAL_FILTERS: InboxFilters = {
  unreadFirst: true,
  showRead: true,
  showCompleted: false,
  announcementsOnly: false,
};

// Mirrors ShellPopovers' desktop resolver: target_path values are full URLs with no matching
// per-resource route, only the section-level route exists, so this lands on the section rather
// than risking an unmatched path.
const SECTION_PATH_PREFIXES: readonly (readonly [string, 'room' | 'act' | 'live' | 'rep'])[] = [
  ['/teacher/classes', 'room'],
  ['/student/classes', 'room'],
  ['/teacher/activities', 'act'],
  ['/student/activities', 'act'],
  ['/teacher/live', 'live'],
  ['/teacher/reports', 'rep'],
  ['/student/reports', 'rep'],
];

function resolveInboxItemSection(
  targetPath: string | undefined,
): 'room' | 'act' | 'live' | 'rep' | null {
  if (!targetPath) return null;
  return SECTION_PATH_PREFIXES.find(([prefix]) => targetPath.startsWith(prefix))?.[1] ?? null;
}

export function MobileInboxPage({
  onBack,
  showBadgeCount,
  onShowBadgeCountChange,
}: {
  onBack(): void;
  showBadgeCount: boolean;
  onShowBadgeCountChange(value: boolean): void;
}) {
  const t = useTranslator();
  const contextId = useActiveContextId();
  const { session } = useWorkspaceSession();
  const query = useInbox(contextId, session?.portalAudience, true);
  const markRead = useMarkInboxRead(contextId, session?.portalAudience);
  const markAllRead = useMarkAllInboxRead(contextId, session?.portalAudience);
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [filterOpen, setFilterOpen] = useState(false);
  const filterRef = useRef<HTMLDivElement>(null);
  const [tab, setTab] = useState<'in' | 'nw'>('in');
  const productUpdates = useProductUpdatesCursor();
  const language = useWorkspaceUiStore((state) => state.language);

  useEffect(() => {
    if (!filterOpen) return;
    const close = (event: PointerEvent) => {
      if (event.target instanceof Node && filterRef.current?.contains(event.target)) return;
      setFilterOpen(false);
    };
    document.addEventListener('pointerdown', close, true);
    return () => document.removeEventListener('pointerdown', close, true);
  }, [filterOpen]);

  // SH-F09: mark-seen fires when this page closes (unmounts -- MobileWorkspaceShell only renders
  // MobileInboxPage while utilityPage === 'inbox') while 有什麼新的 was the active tab, never
  // merely on switching to it. Same ref-cleanup shape as Desktop's InboxPopover.
  const latestTab = useRef(tab);
  latestTab.current = tab;
  const latestMarkAllSeen = useRef(productUpdates.markAllSeen);
  latestMarkAllSeen.current = productUpdates.markAllSeen;
  useEffect(() => {
    return () => {
      if (latestTab.current === 'nw') latestMarkAllSeen.current();
    };
  }, []);

  const hasUnread = (query.data?.items ?? []).some((item) => !item.isRead);

  const items = useMemo(() => {
    const visible = [...(query.data?.items ?? [])].filter((item) => {
      if (!filters.showRead && item.isRead) return false;
      if (!filters.showCompleted && item.isCompleted) return false;
      if (filters.announcementsOnly && item.type !== 'announcement') return false;
      return true;
    });
    return visible.sort((a, b) => {
      if (filters.unreadFirst && a.isRead !== b.isRead) return a.isRead ? 1 : -1;
      return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
    });
  }, [filters, query.data]);

  return (
    <MobilePageFrame
      actions={
        tab === 'in' ? (
          <>
            {hasUnread ? (
              <button
                className="mobile-inbox-mark-all"
                disabled={markAllRead.isPending}
                type="button"
                onClick={() => markAllRead.mutate()}
              >
                {t('全部標為已讀')}
              </button>
            ) : null}
            <div className="mobile-inbox-filter" ref={filterRef}>
              <button
                aria-expanded={filterOpen}
                aria-haspopup="menu"
                className="mobile-inbox-filter__trigger"
                type="button"
                onClick={() => setFilterOpen((open) => !open)}
              >
                <FilterIcon />
                {t('篩選')}
              </button>
              {filterOpen ? (
                <div className="mobile-inbox-filter__menu" role="menu">
                  <FilterToggle
                    checked={filters.unreadFirst}
                    label={t('未讀優先')}
                    onChange={() =>
                      setFilters((current) => ({ ...current, unreadFirst: !current.unreadFirst }))
                    }
                  />
                  <FilterToggle
                    checked={filters.showRead}
                    label={t('顯示已讀')}
                    onChange={() =>
                      setFilters((current) => ({ ...current, showRead: !current.showRead }))
                    }
                  />
                  <FilterToggle
                    checked={filters.showCompleted}
                    label={t('顯示已完成')}
                    onChange={() =>
                      setFilters((current) => ({
                        ...current,
                        showCompleted: !current.showCompleted,
                      }))
                    }
                  />
                  <FilterToggle
                    checked={filters.announcementsOnly}
                    label={t('只看公告')}
                    onChange={() =>
                      setFilters((current) => ({
                        ...current,
                        announcementsOnly: !current.announcementsOnly,
                      }))
                    }
                  />
                  <div className="mobile-inbox-filter__separator" />
                  <FilterToggle
                    checked={showBadgeCount}
                    label={t('顯示未讀數量')}
                    onChange={() => onShowBadgeCountChange(!showBadgeCount)}
                  />
                </div>
              ) : null}
            </div>
          </>
        ) : null
      }
      onBack={onBack}
      title={tab === 'in' ? t('收件匣') : t('有什麼新的')}
    >
      <div className="mobile-inbox-page">
        <div className="mobile-inbox-tabs">
          <button className={tab === 'in' ? 'on' : ''} type="button" onClick={() => setTab('in')}>
            {t('收件匣')}
          </button>
          <button className={tab === 'nw' ? 'on' : ''} type="button" onClick={() => setTab('nw')}>
            {t('有什麼新的')}
          </button>
        </div>
        {tab === 'in' && query.isLoading ? <VispoLoading label={t('正在載入收件匣')} /> : null}
        {tab === 'in' && !query.isLoading && items.length === 0 ? (
          <MainEmpty description={t('新的繳交與提醒會顯示在這裡。')} title={t('收件匣是空的')} />
        ) : null}
        {tab === 'in' && !query.isLoading ? (
          <div className="mobile-inbox-list">
            {items.map((item) => (
              <button
                className={`mobile-inbox-row${item.isRead ? ' is-read' : ''}`}
                key={item.id}
                type="button"
                onClick={() => {
                  markRead.mutate(item);
                  openNotificationTarget(item);
                  onBack();
                }}
              >
                <span className={`mobile-inbox-row__dot${item.isUrgent ? ' is-urgent' : ''}`} />
                <span className="mobile-inbox-row__copy">
                  <b>{item.title}</b>
                  <span>{item.subtitle}</span>
                  <time>{formatTimestamp(item.timestamp)}</time>
                </span>
              </button>
            ))}
          </div>
        ) : null}
        {tab === 'nw' && productUpdates.updates.length === 0 ? (
          <MainEmpty description={t('新的功能公告會顯示在這裡。')} title={t('目前沒有產品更新')} />
        ) : null}
        {tab === 'nw' && productUpdates.updates.length > 0 ? (
          <div className="mobile-inbox-list">
            {productUpdates.updates.map((update) => (
              <div
                className={`mobile-inbox-row${productUpdates.unseenIds.has(update.id) ? '' : ' is-read'}`}
                key={update.id}
              >
                <span className="mobile-inbox-row__dot" />
                <span className="mobile-inbox-row__copy">
                  <b>{localizedProductUpdateText(update.title, language)}</b>
                  <span>{localizedProductUpdateText(update.body, language)}</span>
                  <time>{new Date(update.publishedAt).toLocaleDateString()}</time>
                </span>
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </MobilePageFrame>
  );
}

function FilterToggle({
  checked,
  label,
  onChange,
}: {
  checked: boolean;
  label: string;
  onChange(): void;
}) {
  return (
    <button aria-checked={checked} role="menuitemcheckbox" type="button" onClick={onChange}>
      <span aria-hidden="true" className="mobile-inbox-filter__check">
        {checked ? '✓' : ''}
      </span>
      {label}
    </button>
  );
}

function formatTimestamp(value: string): string {
  return new Intl.DateTimeFormat('zh-TW', {
    month: 'numeric',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));
}

function openNotificationTarget(item: InboxItem) {
  if (item.id.startsWith('notif-')) {
    const section = resolveInboxItemSection(getInboxItemTargetPath(item));
    if (section) {
      useWorkspaceUiStore.getState().navigate(section, null);
      useWorkspaceUiStore.setState({ mobileMainOpen: true });
      return;
    }
    useWorkspaceUiStore.getState().notify('這則通知連結的內容目前無法開啟');
    return;
  }
  const classes = useDatasetStore.getState().classes;
  const activities = useDatasetStore.getState().activities;
  const runs = useDatasetStore.getState().runs;
  const ui = useWorkspaceUiStore.getState();
  const classIndex = item.meta?.classId
    ? classes.findIndex((entry) => entry.id === item.meta?.classId)
    : -1;
  const activityId = item.meta?.examId ?? item.meta?.classworkItemId ?? null;
  const activity = activityId ? activities.find((entry) => entry.id === activityId) : undefined;
  const run = activityId
    ? runs.find((entry) => entry.id === activityId || entry.activityId === activityId)
    : undefined;

  if (run) {
    ui.navigate('act', run.id);
    useWorkspaceUiStore.setState({ mobileMainOpen: true });
    return;
  }
  if (activity) {
    ui.navigate('act', activity.id);
    useWorkspaceUiStore.setState({ mobileMainOpen: true });
    return;
  }
  if (classIndex >= 0) {
    ui.navigate('room', `c${classIndex}`);
    useWorkspaceUiStore.setState({ mobileMainOpen: true });
    return;
  }
  ui.notify('這則通知連結的內容目前無法開啟');
}
